import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '../store';
import { logout } from '../store/slices/authSlice';

/**
 * Axios API Client Configuration
 * Configured for Civilhealth Healthcare Response Management System.
 *
 * @author Civilhealth Development Team
 */

// Create axios instance - use full URL to bypass Vite proxy issues
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8082/api';

const api = axios.create({
  baseURL: `${API_BASE_URL}/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth?.accessToken || localStorage.getItem('accessToken');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Phase-2 modules read the acting user from the X-User-Name header.
    // Attach the logged-in username so create/update/upload calls work from the UI.
    let username = state.auth?.user?.username as string | undefined;
    if (!username) {
      try {
        const userStr = localStorage.getItem('user');
        username = userStr ? (JSON.parse(userStr)?.username as string | undefined) : undefined;
      } catch {
        username = undefined;
      }
    }
    if (username && config.headers) {
      config.headers['X-User-Name'] = username;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;

      // Handle 401 Unauthorized
      if (status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        store.dispatch(logout());

        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      // Handle 403 Forbidden
      if (status === 403) {
        console.error('Access denied. You do not have permission to perform this action.');
      }

      // Handle 500 Server Error
      if (status >= 500) {
        console.error('Server error. Please try again later.');
      }
    } else if (error.request) {
      console.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

export default api;